import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import { ModalManager } from '../interfaces/modal-manager.interface';

import { ModalManagerService } from '../services/modal-manager-service';

/**
 *
 * Modal Base Component
 *
 * Input Properties:
 *
 *  extraModalClasses: class names to be added to modal-dialog div
 *  focusElementId: id of element to initially set focus
 *  modalBaseClass: class for the surrounding div
 *  open: Whether the modal should be open
 *  requiresAction: Whether the modal requires an explict action (X, submit, cancel) to close.
 *                  Modal will not automatically close when user clicks outside the modal's content if true
 *
 *
 * Output Properties:
 *
 *  closeModalHandler: EventEmitter that emits when the modal is closed
 *
 */

@Component({ standalone: false, selector: 'ycn-modal-base', templateUrl: './modal-base.template.html' })
export class ModalBaseComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() extraModalClasses!: string;

    @Input() focusElementId!: string;

    @Input() modalBaseClass!: string;

    @Input() open!: boolean;

    @Input() requiresAction: boolean = false;

    @Output() closeModalHandler = new EventEmitter<void>();

    @ViewChild('modalBaseDiv') modalBaseDiv!: ElementRef;

    modalManager: ModalManager = { open() {}, close() {} };

    constructor(private modalManagerService: ModalManagerService, private ngZone: NgZone) {}

    ngAfterViewInit(): void {
        /**
         * This lifecycle hook is called after Angular has fully initialized the view, meaning that modalBaseDiv has
         * been initialized and we can now get its modal manager
         */
        const closeHandler = (): void => {
            // Re-enter angular zone so change detection runs when to the `open` property is updated
            // and the modal can close properly
            this.ngZone.run(() => {
                if (this.open) {
                    this.closeModalHandler.emit();
                }
            });
        };
        this.modalManager = this.modalManagerService.getModalManager(
            this.modalBaseDiv.nativeElement,
            closeHandler,
            this.requiresAction
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.open && !changes.open.isFirstChange()) {
            const { currentValue, previousValue } = changes.open;
            if (currentValue && !previousValue) {
                this.modalManager.open();
                setTimeout(() => {
                    if (this.focusElementId) {
                        const el: HTMLElement | null = document.querySelector(`.modal.show #${this.focusElementId}`);
                        if (el) el.focus();
                    }
                });
            }
            if (previousValue && !currentValue) this.modalManager.close();
        }
    }

    ngOnDestroy(): void {
        if (this.open) this.modalManager.close();
    }
}
